import { apiService } from 'core/services/apiService';
import { paginationService } from 'core/services/paginationService';

import {
	Organization,
	CreateOrganizationRequest,
	UpdateOrganizationRequest,
	GetOrganizationsParams,
	GetOrganizationsResponse,
	Days,
	ApiDayPart,
} from 'types';

// Transform API response to Organization interface
const transformOrganizationResponse = (apiOrg: any): Organization => {
	return {
		id: apiOrg.id,
		name: apiOrg.name,
		identifier: apiOrg.identifier || '',
		uuid: apiOrg.uuid || '',
		addressLine1: apiOrg.addressLine1,
		city: apiOrg.city,
		postcode: apiOrg.postcode,
		state: apiOrg.state,
		country: apiOrg.country,
		timeZone: apiOrg.timeZone || '',
		products: apiOrg.products || [],
		organization_administrator: {
			name: apiOrg.administratorFirstName && apiOrg.administratorLastName 
				? `${apiOrg.administratorFirstName} ${apiOrg.administratorLastName}`.trim()
				: apiOrg.organization_administrator?.name || '',
			email: apiOrg.administratorEmail || apiOrg.organization_administrator?.email || ''
		},
		start_of_day: apiOrg.startOfDay || apiOrg.start_of_day || '05:00',
		start_of_week: apiOrg.startOfWeek || apiOrg.start_of_week || 'Sunday',
		day_parts: apiOrg.day_parts || [
			{
				id: 'daypart-1',
				name: 'Business Hours',
				startTime: '05:00',
				endTime: '05:00',
			},
		],
		// Handle fiscal calendar fields
		fiscalStartOfYear: apiOrg.fiscalStartOfYear,
		fiscalCalendarType: apiOrg.fiscalCalendarType,
		fiscalPeriodType: apiOrg.fiscalPeriodType,
		fiscalPeriods: apiOrg.fiscalPeriods,
		created_at: apiOrg.created_at,
		updated_at: apiOrg.updated_at,
		active: apiOrg.active,
		deleted: apiOrg.deleted,
	};
};

// Helper function to convert month name back to number
const convertMonthNameToNumber = (monthName: string): string => {
	const monthMap: { [key: string]: string } = {
		'January': '01',
		'February': '02',
		'March': '03',
		'April': '04',
		'May': '05',
		'June': '06',
		'July': '07',
		'August': '08',
		'September': '09',
		'October': '10',
		'November': '11',
		'December': '12'
	};
	return monthMap[monthName] || '01';
};

// Transform create/update request to API format
const transformOrganizationRequest = (
	org: CreateOrganizationRequest | UpdateOrganizationRequest
) => {
	// Extract administrator first and last name from organization_administrator
	const administratorName = org.organization_administrator?.name || '';
	const nameParts = administratorName.split(' ');
	const administratorFirstName = nameParts[0] || '';
	const administratorLastName = nameParts.slice(1).join(' ') || '';
	
	// Transform products array to API format
	const products = Array.isArray(org.products) 
		? org.products.map(product => 
			typeof product === 'object' && product.id 
				? `api/products/${product.id}` 
				: product
		  )
		: [];

	// Convert fiscalStartOfYear to month name
	const monthMap: { [key: string]: string } = {
		'01': 'January',
		'02': 'February', 
		'03': 'March',
		'04': 'April',
		'05': 'May',
		'06': 'June',
		'07': 'July',
		'08': 'August',
		'09': 'September',
		'10': 'October',
		'11': 'November',
		'12': 'December'
	};
	
	const startOfYear = org.fiscalStartOfYear 
		? monthMap[org.fiscalStartOfYear] || org.fiscalStartOfYear
		: 'January';

	return {
		name: org.name,
		administratorFirstName,
		administratorLastName,
		administratorEmail: org.organization_administrator?.email || '',
		products,
		startOfWeek: org.start_of_week || '',
		startOfDay: org.start_of_day || '05:00',
		startOfYear,
		// Fiscal calendar fields
		fiscalCalendarType: org.fiscalCalendarType,
		fiscalPeriodType: org.fiscalPeriodType,
		fiscalPeriods: org.fiscalPeriods,
		fiscalStartOfYear: org.fiscalStartOfYear,
		// Keep existing fields for backward compatibility
		addressLine1: org.addressLine1,
		city: org.city,
		postcode: org.postcode,
		state: org.state,
		country: org.country,
		timeZone: org.timeZone,
		first_day_of_the_week: org.first_day_of_the_week,
	};
};

/**
 * Get all organizations with optional filtering and pagination
 */
export const getOrganizations = async (
	params: GetOrganizationsParams = {}
): Promise<GetOrganizationsResponse> => {
	try {
		const { orderBy = 'id', direction = 'ASC', limit = 50, page = 1 } = params;

		// Use paginationService to automatically fetch all pages
		const response = await paginationService.getWithHandler(
			`/organizations?orderBy=${orderBy}&direction=${direction}`,
			undefined,
			limit
		);

		// Handle different response structures
		let organizations: Organization[] = [];
		let meta = {
			total: 0,
			page: page,
			limit: limit,
		};

		if (response.member && Array.isArray(response.member)) {
			organizations = response.member.map(transformOrganizationResponse);
			meta = {
				total: response.total || response.member.length,
				page: response.page || page,
				limit: response.limit || limit,
			};
		} else if (Array.isArray(response)) {
			organizations = response.map(transformOrganizationResponse);
			meta.total = response.length;
		}

		return {
			data: organizations,
			meta,
		};
	} catch (error: any) {
		console.error('Failed to fetch organizations:', error);
		throw new Error(error.message || 'Failed to fetch organizations');
	}
};

/**
 * Get a single organization by ID
 */
export const getOrganization = async (organizationId: number): Promise<Organization> => {
	try {
		const response = apiService.responseHandler(
			await apiService.get(`/organizations/${organizationId}`)
		);

		return transformOrganizationResponse(response.data || response);
	} catch (error: any) {
		console.error(`Failed to fetch organization ${organizationId}:`, error);
		throw new Error(error.message || 'Failed to fetch organization');
	}
};

/**
 * Create a new organization
 */
export const createOrganization = async (
	orgData: CreateOrganizationRequest
): Promise<Organization> => {
	try {
		const payload = transformOrganizationRequest(orgData);

		const response = apiService.responseHandler(await apiService.post('/organizations', payload));

		return transformOrganizationResponse(response.data || response);
	} catch (error: any) {
		console.error('Failed to create organization:', error);
		throw new Error(error.message || 'Failed to create organization');
	}
};

/**
 * Update an existing organization
 */
export const updateOrganization = async (
	orgData: UpdateOrganizationRequest
): Promise<any> => {
	try {
		const { id, ...updatePayload } = orgData;
		const payload = transformOrganizationRequest({ ...updatePayload, id });
		const response = apiService.responseHandler(
			await apiService.put(`/organizations/${id}`, payload)
		);

		return;
	} catch (error: any) {
		console.error(`Failed to update organization ${orgData.id}:`, error);
		throw new Error(error.message || 'Failed to update organization');
	}
};

/**
 * Delete an organization
 */
export const deleteOrganization = async (organizationId: number): Promise<void> => {
	try {
		await apiService.delete(`/organizations/${organizationId}`);
	} catch (error: any) {
		console.error(`Failed to delete organization ${organizationId}:`, error);
		throw new Error(error.message || 'Failed to delete organization');
	}
};

/**
 * Get organisation types for dropdown
 */
export const getOrganisationTypes = async (): Promise<{ id: number; name: string }[]> => {
	try {
		const response = apiService.responseHandler(
			await apiService.get('/organisation-types', {
				params: {
					orderBy: 'name',
					direction: 'ASC',
					limit: 100,
					page: 1,
				},
			})
		);

		return response.items || response || [];
	} catch (error: any) {
		console.error('Failed to fetch organisation types:', error);
		throw new Error(error.message || 'Failed to fetch organisation types');
	}
};

/**
 * Get brands for dropdown
 */
export const getBrands = async (): Promise<{ label: string }[]> => {
	try {
		const response = apiService.responseHandler(
			await apiService.get('/brands', {
				params: {
					orderBy: 'label',
					direction: 'ASC',
					limit: 100,
					page: 1,
				},
			})
		);

		return response.items || response || [];
	} catch (error: any) {
		console.error('Failed to fetch brands:', error);
		throw new Error(error.message || 'Failed to fetch brands');
	}
};

/**
 * Get time zones for dropdown
 */
export const getTimeZones = async (): Promise<{ id: number; label: string }[]> => {
	try {
		const response = apiService.responseHandler(
			await apiService.get('/time_zones', {
				params: {
					orderBy: 'label',
					direction: 'ASC',
					limit: 500,
					page: 1,
				},
			})
		);

		return response.items || response || [];
	} catch (error: any) {
		console.error('Failed to fetch time zones:', error);
		throw new Error(error.message || 'Failed to fetch time zones');
	}
};

/**
 * Get days for dropdown
 */
export const getDays = async (): Promise<Days[]> => {
	try {
		const response = await paginationService.getWithHandler(
			'/days?orderBy=position&direction=ASC',
			undefined,
			100
		);
		const days = response.member || response?.['hydra:member'] || [];
		return days.sort((a: Days, b: Days) => +a.position - +b.position);
	} catch (error: any) {
		console.error('Failed to fetch days:', error);
		throw new Error(error.message || 'Failed to fetch days');
	}
};

/**
 * Get day parts for a specific organization
 */
export const getDayParts = async (organizationId?: number): Promise<ApiDayPart[]> => {
	try {
		const params: any = {
			orderBy: 'id',
			direction: 'ASC',
			limit: 100,
			page: 1,
		};

		if (organizationId) {
			params.organization = `/api/organizations/${organizationId}`;
		}

		const response = apiService.responseHandler(
			await apiService.get('/day_parts', { params })
		);

		return response.member || response.items || response || [];
	} catch (error: any) {
		console.error('Failed to fetch day parts:', error);
		throw new Error(error.message || 'Failed to fetch day parts');
	}
};

/**
 * Create a new day part
 */
export const createDayPart = async (dayPartData: {
	organization: string;
	partName: string;
	startTime: string;
	endTime: string;
}): Promise<ApiDayPart> => {
	try {
		const response = apiService.responseHandler(
			await apiService.post('/day_parts', dayPartData)
		);

		return response.data || response;
	} catch (error: any) {
		console.error('Failed to create day part:', error);
		throw new Error(error.message || 'Failed to create day part');
	}
};

/**
 * Update an existing day part
 */
export const updateDayPart = async (
	dayPartId: number,
	dayPartData: {
		organization: string;
		partName: string;
		startTime: string;
		endTime: string;
	}
): Promise<ApiDayPart> => {
	try {
		const response = apiService.responseHandler(
			await apiService.put(`/day_parts/${dayPartId}`, dayPartData)
		);

		return response.data || response;
	} catch (error: any) {
		console.error(`Failed to update day part ${dayPartId}:`, error);
		throw new Error(error.message || 'Failed to update day part');
	}
};

/**
 * Delete a day part
 */
export const deleteDayPart = async (dayPartId: number): Promise<void> => {
	try {
		await apiService.delete(`/day_parts/${dayPartId}`);
	} catch (error: any) {
		console.error(`Failed to delete day part ${dayPartId}:`, error);
		throw new Error(error.message || 'Failed to delete day part');
	}
};

// Export all functions as a service object for easier importing
export const organizationSettingsService = {
	getOrganizations,
	getOrganization,
	createOrganization,
	updateOrganization,
	deleteOrganization,
	getOrganisationTypes,
	getBrands,
	getTimeZones,
	getDays,
	getDayParts,
	createDayPart,
	updateDayPart,
	deleteDayPart,
};

export default organizationSettingsService;
